import { useEffect, useCallback, useRef } from 'react';
import { useUnityBuildStore } from '../../stores/store';
import { useStore } from 'zustand';
import { useHandleGlobalStateState } from '../../hooks/useHandleGlobalState';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { KeyboardHandlers, KeyboardShortcut } from '../types/eventTypes';
import { useAnnotationStore } from '../../components/annotations/annotationStore';
import { useKeyboardDebugger } from '../../utils/keyboardDebugger';
import { useSelectionStore } from '../../stores/SelectionStore';

/**
 * Properties for useKeyboardEvents hook
 */
export interface KeyboardEventProps {
  /** Reference to SVG element */
  svgRef: React.RefObject<SVGSVGElement>;

  /** Whether keyboard events are enabled */
  enabled?: boolean;
}

/**
 * Custom hook for handling keyboard events in the SVG editor
 *
 * This hook provides a comprehensive system for keyboard interactions,
 * integrating with global state and providing shortcuts for common operations.
 */
export const useKeyboardEvents = ({
  svgRef,
  enabled = true,
}: KeyboardEventProps): KeyboardHandlers => {
  const keyboardStore = useStore(useKeyBoardEventStore);
  const handleGlobalState = useHandleGlobalStateState();
  const keyboardDebugger = useKeyboardDebugger();

  // Store shortcuts directly in this hook
  const shortcutsRef = useRef<KeyboardShortcut[]>([]);

  /**
   * Register a new shortcut
   */
  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    // Check if shortcut already exists
    const exists = shortcutsRef.current.some(s => s.key === shortcut.key);

    if (!exists) {
      shortcutsRef.current = [...shortcutsRef.current, shortcut];
    }
  }, []);

  /**
   * Unregister an existing shortcut
   */
  const unregisterShortcut = useCallback((key: string) => {
    shortcutsRef.current = shortcutsRef.current.filter(s => s.key !== key);
  }, []);

  /**
   * Handle keyboard events directly
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Skip keyboard events if viewing extension
      if (useUnityBuildStore.getState().isViewingExtension) {
        console.log('🚫 Skipping keyboard events - viewing extension');
        return;
      }

      // Log all key presses for debugging
      keyboardDebugger.logEvent(event, 'keydown');

      // Skip shortcuts if typing in a text input or text area
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement ||
        document.activeElement?.getAttribute('role') === 'textbox' ||
        document.activeElement?.getAttribute('contenteditable') === 'true'
      ) {
        // Allow special keys like Esc to still work even in text inputs
        if (event.key !== 'Escape') {
          console.log('🚫 Skipping shortcut - typing in input element');
          return;
        }
      }

      // Find and execute matching shortcuts
      let shortcutExecuted = false;
      shortcutsRef.current.forEach(shortcut => {
        if (shortcut.key.toLowerCase() === event.key.toLowerCase()) {
          console.log('✅ Executing shortcut:', shortcut.description);
          event.preventDefault();
          event.stopPropagation();
          shortcut.action();
          shortcutExecuted = true;
        }
      });

      if (!shortcutExecuted) {
        console.log('ℹ️ No matching shortcut found for key:', event.key);
      }
    },
    [enabled, keyboardDebugger]
  );

  /**
   * Toggle continuous mode (points are automatically included in count)
   */
  const toggleContinuousMode = useCallback(() => {
    const { continousMode, setContinousMode } = useKeyBoardEventStore.getState();
    setContinousMode(!continousMode);
  }, []);

  /**
   * Toggle snap mode (points snap to grid or angles)
   */
  const toggleSnapMode = useCallback(() => {
    const { snapMode, setSnapMode } = useKeyBoardEventStore.getState();
    setSnapMode(!snapMode);
  }, []);

  /**
   * Toggle crosshair visibility
   */
  const toggleCrosshair = useCallback(() => {
    const { enableCrosshair, setEnableCrosshair } = useKeyBoardEventStore.getState();
    setEnableCrosshair(!enableCrosshair);
  }, []);

  /**
   * Toggle takeoff palette visibility
   */
  const toggleTakeoffPalette = useCallback(() => {
    const { isViewingTakeoffPallette, setIsViewingTakeoffPallette } =
      useKeyBoardEventStore.getState();
    setIsViewingTakeoffPallette(!isViewingTakeoffPallette);
  }, []);

  /**
   * Toggle chat window visibility
   */
  const toggleChatWindow = useCallback(() => {
    const { showChatWindow, setShowChatWindow } = useKeyBoardEventStore.getState();
    setShowChatWindow(!showChatWindow);
  }, []);

  /**
   * Toggle measurement mode
   */
  const toggleMeasurementMode = useCallback(() => {
    const { isMeasuring, setIsMeasuring } = useKeyBoardEventStore.getState();
    setIsMeasuring(!isMeasuring);
  }, []);

  /**
   * Toggle note creation mode
   */
  const toggleNoteCreationMode = useCallback(() => {
    // Get the annotation store state directly for most accurate state
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    // Use explicit logic to toggle state
    if (currentlyAnnotating && currentType === 'text') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('text');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Toggle rectangle creation mode
   */
  const toggleRectangleMode = useCallback(() => {
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    if (currentlyAnnotating && currentType === 'rectangle') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('rectangle');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Toggle ellipse creation mode
   */
  const toggleEllipseMode = useCallback(() => {
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    if (currentlyAnnotating && currentType === 'ellipse') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('ellipse');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Toggle line creation mode
   */
  const toggleLineMode = useCallback(() => {
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    if (currentlyAnnotating && currentType === 'line') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('line');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Toggle polyline creation mode
   */
  const togglePolylineMode = useCallback(() => {
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    if (currentlyAnnotating && currentType === 'polyline') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('polyline');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Toggle polygon creation mode
   */
  const togglePolygonMode = useCallback(() => {
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    if (currentlyAnnotating && currentType === 'polygon') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('polygon');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Toggle arrow creation mode
   */
  const toggleArrowMode = useCallback(() => {
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    if (currentlyAnnotating && currentType === 'arrow') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('arrow');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Toggle highlighter creation mode
   */
  const toggleHighlighterMode = useCallback(() => {
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    if (currentlyAnnotating && currentType === 'highlighter') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('highlighter');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Toggle hyperlink creation mode
   */
  const toggleHyperlinkMode = useCallback(() => {
    const annotationStore = useAnnotationStore.getState();
    const currentlyAnnotating = annotationStore.isAnnotating;
    const currentType = annotationStore.activeAnnotationType;

    if (currentlyAnnotating && currentType === 'hyperlink') {
      annotationStore.setActiveAnnotationType(null);
      annotationStore.setIsAnnotating(false);
    } else {
      annotationStore.setActiveAnnotationType('hyperlink');
      annotationStore.setIsAnnotating(true);
    }
  }, []);

  /**
   * Clear all measurements
   */
  const clearAllMeasurements = useCallback(() => {
    const { setMeasuredPoints } = useUnityBuildStore.getState();
    setMeasuredPoints([]);
  }, []);

  /**
   * Toggle line measurements visibility
   */
  const toggleLineMeasurements = useCallback(() => {
    const { showLineMeasurements, setShowLineMeasurements } = useUnityBuildStore.getState();
    setShowLineMeasurements(!showLineMeasurements);
  }, []);

  /**
   * Toggle point measurements visibility
   */
  const togglePointMeasurements = useCallback(() => {
    const { showPointMeasurements, setShowPointMeasurements } = useUnityBuildStore.getState();
    setShowPointMeasurements(!showPointMeasurements);
  }, []);

  /**
   * Toggle lasso selection mode
   */
  const toggleLassoMode = useCallback(() => {
    const { isLassoMode, setIsLassoMode, setIsLassoDrawing, setLassoPoints } =
      useUnityBuildStore.getState();
    const next = !isLassoMode;
    setIsLassoMode(next);
    setIsLassoDrawing(false);
    setLassoPoints([]);
  }, []);

  /**
   * Toggle flip/mirror mode
   */
  const toggleFlipMode = useCallback(() => {
    const { toggleFlip } = useSelectionStore.getState();
    toggleFlip();
  }, []);

  /**
   * Toggle dimensional mode
   */
  const toggleDimensionalMode = useCallback(() => {
    const { isDimensionalMode, setIsDimensionalMode } = useAnnotationStore.getState();
    setIsDimensionalMode(!isDimensionalMode);
  }, []);

  /**
   * Toggle annotation visibility
   */
  const toggleAnnotationVisibility = useCallback(() => {
    const { annotationVisibilityMode, setAnnotationVisibilityMode } = useAnnotationStore.getState();
    const modes: ('all' | 'mine' | 'none')[] = ['all', 'mine', 'none'];
    const currentIndex = modes.indexOf(annotationVisibilityMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setAnnotationVisibilityMode(modes[nextIndex]);
  }, []);

  /**
   * Toggle cursor tooltips
   */
  const toggleCursorTooltips = useCallback(() => {
    const { showCursorTooltip, setShowCursorTooltip } = useAnnotationStore.getState();
    setShowCursorTooltip(!showCursorTooltip);
  }, []);

  /**
   * Reset current takeoff state
   */
  const resetTakeoff = useCallback(() => {
    // Dispatch event to clear any paste preview elements
    document.dispatchEvent(new CustomEvent('app:clearpastepreview'));

    // Set all annotation states to false
    const annotationStore = useAnnotationStore.getState();
    annotationStore.setActiveAnnotationType(null);
    annotationStore.setIsAnnotating(false);
    annotationStore.setAnnotationStartingPoint(null);

    handleGlobalState({
      resetOption: 'resetTakeoff',
    });
  }, [handleGlobalState]);

  /**
   * End measuring mode
   */
  const endMeasuringMode = useCallback(() => {
    if (keyboardStore.isMeasuring) {
      // Only set measuring mode to false but don't clear the measured points
      // This allows measurements to persist on screen until explicitly cleared
      keyboardStore.setIsMeasuring(false);
    }
  }, [keyboardStore]);

  // Register all keyboard shortcuts and set up global event listener
  useEffect(() => {
    if (!enabled && useUnityBuildStore.getState().isViewingExtension) return;

    // Define all shortcuts
    const shortcuts: KeyboardShortcut[] = [
      {
        key: 'Control',
        action: toggleContinuousMode,
        description: 'Toggle continuous mode',
      },
      {
        key: 'Shift',
        action: toggleSnapMode,
        description: 'Toggle snap mode',
      },
      {
        key: 't',
        action: toggleCrosshair,
        description: 'Toggle crosshair visibility',
      },
      {
        key: 'o',
        action: toggleTakeoffPalette,
        description: 'Toggle takeoff palette',
      },
      {
        key: 'c',
        action: toggleChatWindow,
        description: 'Toggle chat window',
      },
      {
        key: 'm',
        action: toggleMeasurementMode,
        description: 'Toggle measurement mode',
      },
      {
        key: 'Enter',
        action: () => {
          // Check if we're in measuring mode first
          if (keyboardStore.isMeasuring) {
            endMeasuringMode();
          } else {
            console.log('🚫 Skipping submit - not in measuring mode');
            // submitCurrentAssembly();
          }
        },
        description: 'Submit assembly or end measuring mode',
      },
      {
        key: 'Escape',
        action: resetTakeoff,
        description: 'Reset takeoff state',
      },
      {
        key: 'n',
        action: toggleNoteCreationMode,
        description: 'Create Note',
      },
      // Annotation shortcuts
      {
        key: 'r',
        action: toggleRectangleMode,
        description: 'Toggle rectangle creation mode',
      },
      {
        key: 'e',
        action: toggleEllipseMode,
        description: 'Toggle ellipse creation mode',
      },
      {
        key: 'l',
        action: toggleLineMode,
        description: 'Toggle line creation mode',
      },
      {
        key: 'p',
        action: togglePolylineMode,
        description: 'Toggle polyline creation mode',
      },
      {
        key: 'g',
        action: togglePolygonMode,
        description: 'Toggle polygon creation mode',
      },
      {
        key: 'a',
        action: toggleArrowMode,
        description: 'Toggle arrow creation mode',
      },
      {
        key: 'h',
        action: toggleHighlighterMode,
        description: 'Toggle highlighter creation mode',
      },
      {
        key: 'k',
        action: toggleHyperlinkMode,
        description: 'Toggle hyperlink creation mode',
      },
      // Additional toolbar shortcuts
      {
        key: 'x',
        action: clearAllMeasurements,
        description: 'Clear all measurements',
      },
      {
        key: 'q',
        action: toggleLineMeasurements,
        description: 'Toggle line measurements visibility',
      },
      {
        key: 'w',
        action: togglePointMeasurements,
        description: 'Toggle point measurements visibility',
      },
      {
        key: 's',
        action: toggleLassoMode,
        description: 'Toggle lasso selection mode',
      },
      {
        key: 'f',
        action: toggleFlipMode,
        description: 'Toggle flip/mirror mode',
      },
      {
        key: 'd',
        action: toggleDimensionalMode,
        description: 'Toggle dimensional mode',
      },
      {
        key: 'v',
        action: toggleAnnotationVisibility,
        description: 'Toggle annotation visibility',
      },
      {
        key: 'i',
        action: toggleCursorTooltips,
        description: 'Toggle cursor tooltips',
      },
    ];

    // Register each shortcut
    shortcuts.forEach(shortcut => registerShortcut(shortcut));

    // Set up global keyboard event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      shortcuts.forEach(shortcut => unregisterShortcut(shortcut.key));
      window.removeEventListener('keydown', handleKeyDown);
    };
    // Reduce dependencies to only those that would actually require re-registering shortcuts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, registerShortcut, unregisterShortcut, handleKeyDown]);

  return {
    registerShortcut,
    unregisterShortcut,
  };
};
